import { useEffect, useRef, useState } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Briefcase, Bell, CircleDollarSign, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BottomTabBar from '../shared/ui/BottomTabBar';
import { LoadingView } from '../shared/ui';
import { listenToAcceptedOrders, listenToAvailableOrders, activeDeliveries } from '../shared/services/orderService';
import { orderMatchesDriverZone } from '../shared/models';
import { listenToNotifications } from '../shared/services/notificationService';
import InAppNotificationBanner from '../../../components/InAppNotificationBanner';

// Mirrors iOS DriverDashboardView TabView: Work (briefcase.fill, badge =
// activeDeliveries + availableOrders) · Earnings (dollarsign.circle)
// · Notifications (bell.fill, badge = unread) · Profile (person.fill).
export default function DriverLayout() {
  const { user, userProfile, driverId, loading, roleError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [workBadge, setWorkBadge] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bannerNotif, setBannerNotif] = useState(null);

  // iOS: Work tab badge = activeDeliveries.count + availableOrders.count
  useEffect(() => {
    if (!driverId) return;
    let accepted = [];
    let available = [];
    const recompute = () =>
      setWorkBadge(activeDeliveries(accepted).length + available.length);
    const unsubA = listenToAcceptedOrders(driverId, (list) => { accepted = list; recompute(); });
    const unsubB = listenToAvailableOrders((list) => {
      // Badge counts what the driver would actually see — zone-filtered too.
      const zone = localStorage.getItem('driverZone') || '';
      available = list.filter((o) => orderMatchesDriverZone(o, zone));
      recompute();
    }, undefined, userProfile?.island);
    return () => { unsubA && unsubA(); unsubB && unsubB(); };
  }, [driverId, userProfile?.island]);

  // iOS: Alerts tab badge = unread notification count + driver-alert sound
  // and an in-app banner on new notifications (after the initial snapshot).
  const lastNotifIdRef = useRef(null);
  const alertAudioRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    return listenToNotifications(user.uid, (list) => {
      const unread = list.filter((n) => !n.read && !n.isRead).length;
      setUnreadCount(unread);
      const newest = list[0];
      if (lastNotifIdRef.current !== null && newest && newest.id !== lastNotifIdRef.current) {
        try {
          if (!alertAudioRef.current) alertAudioRef.current = new Audio('/sounds/driver-alert.m4a');
          alertAudioRef.current.currentTime = 0;
          alertAudioRef.current.play().catch(() => {});
        } catch { /* audio blocked until user interaction */ }
        setBannerNotif(newest);
      }
      lastNotifIdRef.current = newest?.id || lastNotifIdRef.current;
    });
  }, [user]);

  if (loading) {
    return <div className="min-h-full flex items-center justify-center bg-white"><LoadingView label="Loading…" /></div>;
  }
  if (!user || roleError) {
    return <Navigate to="/login" state={{ from: location.pathname, roleError }} replace />;
  }

  const tabs = [
    { to: '/driver/work', label: 'Work', Icon: Briefcase, badge: workBadge },
    { to: '/driver/earnings', label: 'Earnings', Icon: CircleDollarSign },
    { to: '/driver/alerts', label: 'Notifications', Icon: Bell, badge: unreadCount },
    { to: '/driver/profile', label: 'Profile', Icon: User },
  ];

  return (
    <div className="h-screen supports-[height:100dvh]:h-[100dvh] max-w-lg lg:max-w-6xl mx-auto bg-[#F2F2F7] flex flex-col">
      {/* min-h-0 gives children a definite height — WorkPage's full-screen
          map needs it. overflow-y-auto scrolls non-map pages internally. */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-20">
        <Outlet />
      </div>
      <BottomTabBar tabs={tabs} />
      <InAppNotificationBanner
        notification={bannerNotif}
        onTap={() => { setBannerNotif(null); navigate('/driver/alerts'); }}
        onDismiss={() => setBannerNotif(null)}
      />
    </div>
  );
}
